import React from "react";
import { Box, Stack, Typography, alpha } from "@mui/material";
import { keyframes } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import Category from "iconsax-reactjs/dist/esm/Category.js";
import Building4 from "iconsax-reactjs/dist/esm/Building4.js";
import Devices from "iconsax-reactjs/dist/esm/Devices.js";
import Bank from "iconsax-reactjs/dist/esm/Bank.js";
import Car from "iconsax-reactjs/dist/esm/Car.js";
import Like from "iconsax-reactjs/dist/esm/Like.js";
import Briefcase from "iconsax-reactjs/dist/esm/Briefcase.js";
import Setting2 from "iconsax-reactjs/dist/esm/Setting2.js";
import Brush from "iconsax-reactjs/dist/esm/Brush.js";
import Health from "iconsax-reactjs/dist/esm/Health.js";

const ICON_MAP = {
  Category,
  Building4,
  Devices,
  Bank,
  Car,
  Like,
  Briefcase,
  Setting2,
  Brush,
  Health,
};

const FALLBACK_ICON = Category;
const MEDIA_BOX_SIZE = 52;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(22px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spinRing = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

function CategoryIconRenderer({ name, size = 24 }) {
  const IconComponent = (name && ICON_MAP[name]) || FALLBACK_ICON;

  return <IconComponent size={size} variant="Bold" />;
}

export default function CategoryCard({ item, index, inView }) {
  let imageSrc = item.image;
  if (imageSrc) {
    if (
      imageSrc.includes("ChatGPT%20Image%20Feb%2015") ||
      imageSrc.includes("ChatGPT Image Feb 15")
    ) {
      imageSrc = "/Media/categories/category-cleaning.webp";
    } else if (!imageSrc.startsWith("http")) {
      imageSrc =
        (import.meta.env.VITE_FILE_API || "https://ramaapp.com/api/") +
        imageSrc.replace(/^\//, "");
    }
  }

  const hasImage = Boolean(imageSrc);
  const hasIcon = Boolean(item.icon);

  const delay = Math.min(index * 0.06, 0.48);

  let hoverStyles = {};

  if (hasIcon) {
    hoverStyles = {
      "&:hover .cat-icon-box": {
        bgcolor: "primary.main",
        color: "#fff",
      },
    };
  }

  return (
    <Box
      component={RouterLink}
      to={`/category?level=1&id=${item.id}&parentId=${item.id}`}
      sx={{
        position: "relative",
        display: "block",
        textDecoration: "none",
        borderRadius: "18px",

        opacity: inView ? undefined : 0,

        animation: inView
          ? `${fadeUp} .6s cubic-bezier(.2,.7,.2,1) forwards`
          : "none",

        animationDelay: `${delay}s`,

        "&::before": {
          content: '""',
          position: "absolute",
          inset: -2,
          borderRadius: "20px",
          padding: 2,

          background: (theme) =>
            `conic-gradient(
              from 0deg,
              ${theme.palette.primary.main},
              ${theme.palette.secondary.main},
              ${theme.palette.primary.main}
            )`,

          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",

          WebkitMaskComposite: "xor",
          maskComposite: "exclude",

          opacity: 0,
          transition: "opacity .35s ease",

          animation: `${spinRing} 3.2s linear infinite`,

          pointerEvents: "none",
        },
        "&:hover::before": {
          opacity: 1,
        },
        "&:hover .cat-surface": {
          transform: "translateY(-4px)",
        },

        "&:hover .cat-media": {
          transform: "scale(1.06)",
        },

        ...hoverStyles,
      }}
    >
      <Stack
        className="cat-surface"
        alignItems="center"
        justifyContent="flex-start"
        spacing={1}
        sx={{
          position: "relative",
          height: "100%",
          borderRadius: "18px",
          overflow: "hidden",

          bgcolor: "background.paper",

          boxShadow: "0 2px 10px rgba(15,23,42,0.05)",

          px: 1.5,
          py: 2,

          transition:
            "transform .3s cubic-bezier(.2,.7,.2,1), box-shadow .3s ease",

          "&:hover": {
            boxShadow: "0 14px 28px rgba(15,23,42,0.12)",
          },
        }}
      >
        <Box
          className="cat-icon-box"
          sx={{
            width: MEDIA_BOX_SIZE,
            height: MEDIA_BOX_SIZE,

            borderRadius: "14px",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            overflow: "hidden",
            flexShrink: 0,

            bgcolor: hasImage
              ? "transparent"
              : (theme) => alpha(theme.palette.primary.main, 0.1),

            color: "primary.main",

            transition: "background-color .3s ease, color .3s ease",
          }}
        >
          {hasImage ? (
            <Box
              className="cat-media"
              component="img"
              src={imageSrc}
              alt=""
              width={MEDIA_BOX_SIZE}
              height={MEDIA_BOX_SIZE}
              loading="lazy"
              decoding="async"
              sx={{
                width: "100%",
                height: "100%",
                aspectRatio: "1 / 1",
                objectFit: "cover",
                borderRadius: "14px",
                transition: "transform .35s ease",
              }}
            />
          ) : (
            <Box
              className="cat-media"
              sx={{
                display: "flex",
                transition: "transform .35s ease",
              }}
            >
              <CategoryIconRenderer name={item.icon} size={24} />
            </Box>
          )}
        </Box>

        <Typography
          sx={{
            fontFamily: "Dana",
            fontWeight: 700,
            fontSize: 12.5,
            color: "text.primary",
            textAlign: "center",
            lineHeight: 1.45,
          }}
        >
          {item.title}
        </Typography>
      </Stack>
    </Box>
  );
}
